import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";

const QUEUE_KEY = "shs_offline_queue";

const getQueue = () => {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
  } catch {
    return [];
  }
};

const saveQueue = (queue) => {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
};

export function useOfflineQueue(apiBase, getAuthHeaders) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queue, setQueue] = useState(getQueue);
  const [isSyncing, setIsSyncing] = useState(false);
  const syncingRef = useRef(false);

  // Online/offline detection
  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const enqueue = useCallback((action) => {
    const item = {
      id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      timestamp: new Date().toISOString(),
      ...action,
    };
    setQueue((prev) => {
      const next = [...prev, item];
      saveQueue(next);
      return next;
    });
    return item;
  }, []);

  const processQueue = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    setIsSyncing(true);

    const pending = getQueue();
    if (pending.length === 0) {
      syncingRef.current = false;
      setIsSyncing(false);
      return;
    }

    toast.info(`Syncing ${pending.length} queued item${pending.length > 1 ? "s" : ""}...`);
    let synced = 0;
    let failed = [];

    for (const item of pending) {
      try {
        const headers = { ...getAuthHeaders(), "Content-Type": "application/json" };
        let url = `${apiBase}/${item.endpoint}`;
        let options = { method: item.method || "POST", headers };
        if (item.body) options.body = JSON.stringify(item.body);

        const res = await fetch(url, options);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        synced++;
      } catch {
        failed.push(item);
      }
    }

    saveQueue(failed);
    setQueue(failed);
    syncingRef.current = false;
    setIsSyncing(false);

    if (synced > 0) {
      toast.success(`Synced ${synced} item${synced > 1 ? "s" : ""}`);
    }
    if (failed.length > 0) {
      toast.error(`${failed.length} item${failed.length > 1 ? "s" : ""} failed to sync`);
    }

    return synced;
  }, [apiBase, getAuthHeaders]);

  // Sync when coming back online
  useEffect(() => {
    if (isOnline && queue.length > 0 && !syncingRef.current) {
      processQueue();
    }
  }, [isOnline, queue.length, processQueue]);

  const clearQueue = useCallback(() => {
    saveQueue([]);
    setQueue([]);
  }, []);

  const queueCount = queue.length;

  return { isOnline, queue, queueCount, isSyncing, enqueue, processQueue, clearQueue };
}
